import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/Header/Header';
import GlobalFooter from '../../components/Footer/GlobalFooter';
import SeoMeta from '../../components/SeoMeta/SeoMeta';
import { useAuth } from '../auth/hooks/useAuth';
import { getEduPortalUrl } from '../../config/api';
import { EXAM_PACKAGES } from '../../config/pricingCatalog';
import { tierSlugOf } from '../checkout/pendingCheckout';
import { startCheckout } from '../marketing/startCheckout';
import { ORG_PLAN_NOTICE, readSignedInStoredUser, userMayPurchase } from '../checkout/purchaseEligibility';
import './EduPricingPage.css';

export default function EduPricingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const mayPurchase = userMayPurchase(user || readSignedInStoredUser());

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loadingTier, setLoadingTier] = useState(null);

  // Read URL query params on load (e.g. ?exam=tmua)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const examParam = params.get('exam');

    if (examParam) {
      const match = EXAM_PACKAGES.find(p => p.id === examParam.toLowerCase());
      if (match) {
        setSelectedCategory(match.category);
      }
    }
  }, [location.search]);

  const handleCheckout = async (examId, tierKey, isFree) => {
    if (isFree) {
      window.location.href = getEduPortalUrl(`/practice/${examId}`);
      return;
    }

    setLoadingTier(tierKey);
    try {
      // Exams only (Q18 default): the body is {exam, tier}. Sign-in, the
      // organisation refusal and the echo check live in startCheckout.
      await startCheckout({
        tierKey,
        examId,
        surface: 'pricing',
        navigate,
        user: user || readSignedInStoredUser(),
      });
    } catch (err) {
      // startCheckout reports its own failures; this keeps the button usable.
    } finally {
      setLoadingTier(null);
    }
  };

  const filteredPackages = selectedCategory === 'all'
    ? EXAM_PACKAGES
    : EXAM_PACKAGES.filter(p => p.category === selectedCategory);

  // What the account holds decides "current", never active_plan (D:87-88).
  // A user stored without access.owned owns nothing.
  const heldExams = (user || readSignedInStoredUser())?.access?.owned?.exams;
  const holdsPlan = (examId, tierKey) =>
    Array.isArray(heldExams) &&
    heldExams.some((held) => held && held.exam === examId && held.tier === tierSlugOf(tierKey));

  return (
    <div className="edu-pricing-page-container">
      <SeoMeta
        title="All Plans & Pricing — ivori edu Student Portal"
        description="Explore all ivori university admissions test prep suites including LNAT, TMUA, UCAT, ESAT, TARA, and Medical School Interview Simulators."
        canonicalUrl="https://ivoriai.com/edu/pricing"
      />

      <Header variant="edu" />

      <main className="edu-pricing-main">
        {/* Hero Section */}
        <section className="edu-pricing-hero">
          <h1 className="edu-pricing-title">
            Plans & Pricing
          </h1>
          <p className="edu-pricing-subtitle">
            Select a preparation track.
          </p>
          {!mayPurchase && <p className="edu-pricing-subtitle">{ORG_PLAN_NOTICE}</p>}

          {/* Subject Filter Navigation */}
          <div className="edu-pricing-filter-bar">
            <button
              type="button"
              className={`edu-pricing-tab ${selectedCategory === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('all')}
            >
              All
            </button>
            <button
              type="button"
              className={`edu-pricing-tab ${selectedCategory === 'law' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('law')}
            >
              LNAT
            </button>
            <button
              type="button"
              className={`edu-pricing-tab ${selectedCategory === 'maths' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('maths')}
            >
              TMUA
            </button>
            <button
              type="button"
              className={`edu-pricing-tab ${selectedCategory === 'medicine' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('medicine')}
            >
              UCAT
            </button>
            <button
              type="button"
              className={`edu-pricing-tab ${selectedCategory === 'engineering' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('engineering')}
            >
              ESAT
            </button>
            <button
              type="button"
              className={`edu-pricing-tab ${selectedCategory === 'reasoning' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('reasoning')}
            >
              TARA
            </button>
          </div>
        </section>

        {/* Exam Packages Grid */}
        <div className="edu-pricing-packages-section">
          {filteredPackages.map((pkg) => (
            <div key={pkg.id} className="edu-exam-package-card" id={`package-${pkg.id}`}>
              <div className="edu-package-header">
                <h2 className="package-title">{pkg.title}</h2>
              </div>

              {/* 3 Tier Cards Grid */}
              <div className="edu-tiers-grid">
                {pkg.plans.map((plan) => {
                  const isCurrent = holdsPlan(pkg.id, plan.tierKey);

                  return (
                    <div
                      key={plan.tierKey}
                      className={`edu-tier-card ${isCurrent ? 'current-active' : ''}`}
                    >
                      {isCurrent && (
                        <div className="edu-tier-active-pill">Current Plan</div>
                      )}

                      <div className="tier-header">
                        <h3 className="tier-name">{plan.name}</h3>
                        <p className="tier-desc">{plan.description}</p>
                        <div className="tier-price-row">
                          <span className="tier-price">{plan.price}</span>
                          <span className="tier-period">{`/ ${plan.period.replace(/^\/\s*/, '')}`}</span>
                        </div>
                      </div>

                      {/* A free tier only opens the portal; it is not a purchase. */}
                      {(mayPurchase || plan.isFree) && (
                        <div className="tier-action-wrap">
                          <button
                            type="button"
                            className="edu-tier-btn btn-standard"
                            onClick={() => handleCheckout(pkg.id, plan.tierKey, plan.isFree)}
                            disabled={loadingTier === plan.tierKey || isCurrent}
                          >
                            {loadingTier === plan.tierKey
                              ? 'Connecting to Stripe…'
                              : isCurrent
                              ? 'Active in Account'
                              : (plan.cta || 'Get Started')}
                          </button>
                        </div>
                      )}

                      <ul className="tier-features-list">
                        {plan.features.map((feat, idx) => (
                          <li key={idx}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--iv-body)" strokeWidth="2">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            <span>{/^(Everything in|All content in )/i.test(feat) ? <strong>{feat}</strong> : feat}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </main>

      <GlobalFooter variant="edu" />
    </div>
  );
}
